import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import { ContactsSelector } from "../../utils/moduleSelectors/contactSelector";
import {
  createOfferInquiry,
  deleteOfferInquiry,
  fetchOfferInquiries,
  updateOfferInquiry,
} from "../../../api/offer";
import "./offer-workspace.css";
import {
  isOfferFeatureEnabled,
  resolveOfferActionErrorMessage,
} from "./utils";

const STATUSES = [
  "requested",
  "in_progress",
  "offered",
  "declined",
  "won",
  "lost",
];

const EMPTY_FORM = {
  requesting_customer_id: null,
  requesting_company_name: "",
  customer: null,
  company_contact_id: null,
  contact: null,
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  due_at: "",
  notes: "",
};

function toDateInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function formatDate(value, locale) {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function customerLabel(customer) {
  if (!customer) return "";
  return (
    customer.displayTitle ||
    customer.name ||
    customer.company_name ||
    ""
  ).trim();
}

function contactPhone(contact) {
  if (!contact) return "";
  return (
    contact.mobile ||
    contact.phone ||
    contact.mobile_work ||
    ""
  ).trim();
}

function formFromRow(row) {
  const customer =
    row?.requesting_customer?.id
      ? row.requesting_customer
      : row?.requesting_customer_id
        ? {
            id: row.requesting_customer_id,
            name: row.requesting_company_name,
            displayTitle: row.requesting_company_name,
          }
        : null;
  const contact =
    row?.company_contact?.id
      ? row.company_contact
      : row?.company_contact_id
        ? {
            id: row.company_contact_id,
            name: row.contact_name,
            displayTitle: row.contact_name,
            email: row.contact_email,
            mobile: row.contact_phone,
          }
        : null;

  return {
    requesting_customer_id: row?.requesting_customer_id || customer?.id || null,
    requesting_company_name: row?.requesting_company_name || "",
    customer,
    company_contact_id: row?.company_contact_id || contact?.id || null,
    contact,
    contact_name: row?.contact_name || contact?.name || "",
    contact_email: row?.contact_email || contact?.email || "",
    contact_phone: row?.contact_phone || contactPhone(contact) || "",
    due_at: toDateInputValue(row?.due_at),
    notes: row?.notes || "",
  };
}

/**
 * Multi-requestor anbud list on offer overview.
 * Visible only when company flag `offer_inquiries` is enabled.
 */
export default function OfferInquiriesSection({
  offer,
  featureFlags,
  onChanged,
}) {
  const { t, i18n } = useTranslation();
  const flags = featureFlags || offer?.offer_feature_flags || {};
  // Same gating pattern as checklist/revisjoner: empty flags = still loading — hide, don't flash.
  const flagsReady = Object.keys(flags).length > 0;
  const enabled = flagsReady && isOfferFeatureEnabled(flags, "offer_inquiries");

  const [items, setItems] = useState(() =>
    Array.isArray(offer?.inquiries) ? offer.inquiries : []
  );
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [statusRow, setStatusRow] = useState(null);
  const [statusValue, setStatusValue] = useState("requested");

  useEffect(() => {
    if (Array.isArray(offer?.inquiries)) {
      setItems(offer.inquiries);
    }
  }, [offer?.id, offer?.inquiries]);

  useEffect(() => {
    if (!enabled || !offer?.id) return undefined;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const res = await fetchOfferInquiries(offer.id);
        if (cancelled) return;
        if (Array.isArray(res?.data)) {
          setItems(res.data);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, offer?.id]);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const aDue = a?.due_at ? new Date(a.due_at).getTime() : Number.MAX_SAFE_INTEGER;
      const bDue = b?.due_at ? new Date(b.due_at).getTime() : Number.MAX_SAFE_INTEGER;
      if (aDue !== bDue) return aDue - bDue;
      return (a?.id || 0) - (b?.id || 0);
    });
  }, [items]);

  if (!flagsReady || !enabled) return null;

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm(formFromRow(row));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCustomerChange = (event) => {
    const data = event?.target?.data || null;
    const name = customerLabel(data);
    setForm((prev) => ({
      ...prev,
      customer: data,
      requesting_customer_id: data?.id || null,
      requesting_company_name: name,
      contact: null,
      company_contact_id: null,
      contact_name: "",
      contact_email: "",
      contact_phone: "",
    }));
  };

  const handleContactChange = (event) => {
    const data = event?.target?.data || null;
    if (!data?.id) {
      setForm((prev) => ({
        ...prev,
        contact: null,
        company_contact_id: null,
        contact_name: "",
        contact_email: "",
        contact_phone: "",
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      contact: data,
      company_contact_id: data.id,
      contact_name: (data.name || data.displayTitle || "").trim(),
      contact_email: (data.email || "").trim(),
      contact_phone: contactPhone(data),
    }));
  };

  const refreshList = async () => {
    const list = await fetchOfferInquiries(offer.id);
    if (Array.isArray(list?.data)) setItems(list.data);
  };

  const handleSave = async () => {
    if (!offer?.id) return;
    const name = form.requesting_company_name?.trim();
    if (!form.requesting_customer_id || !name) {
      toast.error(t("offers.inquiries.validation.company_required"));
      return;
    }

    const payload = {
      requesting_customer_id: form.requesting_customer_id,
      requesting_company_name: name,
      company_contact_id: form.company_contact_id || null,
      contact_name: form.contact_name?.trim() || null,
      contact_email: form.contact_email?.trim() || null,
      contact_phone: form.contact_phone?.trim() || null,
      due_at: form.due_at ? `${form.due_at}T12:00:00.000Z` : null,
      notes: form.notes?.trim() || null,
    };

    // New inquiries always start as requested; status changes use the status dialog.
    if (!editing?.id) {
      payload.status = "requested";
    }

    setSaving(true);
    try {
      let result;
      if (editing?.id) {
        result = await updateOfferInquiry(offer.id, editing.id, payload);
      } else {
        result = await createOfferInquiry(offer.id, payload);
      }

      if (result?.errors || result?.error || result?.status >= 400) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result,
            "offers.inquiries.save_failed"
          )
        );
        return;
      }

      toast.success(
        editing?.id
          ? t("offers.inquiries.updated")
          : t("offers.inquiries.created")
      );
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);

      await refreshList();
      onChanged?.(result);
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(t, err, "offers.inquiries.save_failed")
      );
    } finally {
      setSaving(false);
    }
  };

  const openStatusDialog = (row) => {
    setStatusRow(row);
    setStatusValue(row?.status || "requested");
  };

  const closeStatusDialog = () => {
    if (saving) return;
    setStatusRow(null);
    setStatusValue("requested");
  };

  const handleStatusSave = async () => {
    if (!offer?.id || !statusRow?.id) return;
    setSaving(true);
    try {
      const result = await updateOfferInquiry(offer.id, statusRow.id, {
        status: statusValue || "requested",
      });
      if (result?.errors || result?.error || result?.status >= 400) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result,
            "offers.inquiries.save_failed"
          )
        );
        return;
      }
      toast.success(t("offers.inquiries.status_updated"));
      setStatusRow(null);
      await refreshList();
      onChanged?.(result);
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(t, err, "offers.inquiries.save_failed")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!offer?.id || !deleteId) return;
    setSaving(true);
    try {
      const result = await deleteOfferInquiry(offer.id, deleteId);
      if (result?.errors || result?.error || result?.status >= 400) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result,
            "offers.inquiries.delete_failed"
          )
        );
        return;
      }
      toast.success(t("offers.inquiries.deleted"));
      setDeleteId(null);
      setItems((prev) => prev.filter((row) => row.id !== deleteId));
      onChanged?.(result);
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(t, err, "offers.inquiries.delete_failed")
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldClass =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]";

  const selectorValues = {
    customer_id: form.requesting_customer_id,
    company_contact_id: form.company_contact_id,
  };

  return (
    <div className="rounded-[9px] border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[13px] font-[650] text-slate-900">
            {t("offers.inquiries.title")}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {t("offers.inquiries.subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#1463C7] px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-[#0B3A78]"
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
          {t("offers.inquiries.add")}
        </button>
      </div>

      {loading && sorted.length === 0 ? (
        <div className="h-20 animate-pulse rounded-lg bg-slate-50" />
      ) : sorted.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500">
          {t("offers.inquiries.empty")}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {sorted.map((row) => (
            <li
              key={row.id}
              className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-semibold text-slate-900">
                    {row.requesting_company_name}
                  </span>
                  <button
                    type="button"
                    onClick={() => openStatusDialog(row)}
                    className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-200"
                    aria-label={t("offers.inquiries.change_status")}
                    title={t("offers.inquiries.change_status")}
                  >
                    {t(`offers.inquiries.status.${row.status}`, {
                      defaultValue: row.status,
                    })}
                  </button>
                </div>
                <p className="mt-0.5 text-xs text-slate-500">
                  {[row.contact_name, row.contact_email, row.contact_phone]
                    .filter(Boolean)
                    .join(" · ") || t("offers.inquiries.no_contact")}
                </p>
                <p className="mt-0.5 text-xs text-slate-400">
                  {t("offers.inquiries.due")}:{" "}
                  {formatDate(row.due_at, i18n.language)}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
                  aria-label={t("offers.inquiries.edit")}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteId(row.id)}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-200 p-2 text-red-600 hover:bg-red-50"
                  aria-label={t("offers.inquiries.delete")}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <PMDialog
        open={dialogOpen}
        onClose={closeDialog}
        title={
          editing
            ? t("offers.inquiries.edit_title")
            : t("offers.inquiries.add_title")
        }
        size="md"
        closeOnOutsideClick={!saving}
      >
        <div className="space-y-3">
          <CustomerSelector
            label={t("offers.inquiries.fields.company")}
            name="customer_id"
            selected={form.customer}
            values={selectorValues}
            setFieldValue={() => {}}
            onChange={handleCustomerChange}
            disabled={saving}
          />
          <ContactsSelector
            label={t("offers.inquiries.fields.contact_name")}
            name="company_contact_id"
            customer={form.customer}
            module_param={form.requesting_customer_id}
            selected={form.contact}
            values={selectorValues}
            setFieldValue={() => {}}
            onChange={handleContactChange}
            disabled={saving || !form.requesting_customer_id}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-slate-600">
                {t("offers.inquiries.fields.contact_email")}
              </span>
              <input
                type="email"
                className={fieldClass}
                value={form.contact_email}
                onChange={(e) => setField("contact_email", e.target.value)}
                disabled={saving}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-slate-600">
                {t("offers.inquiries.fields.contact_phone")}
              </span>
              <input
                className={fieldClass}
                value={form.contact_phone}
                onChange={(e) => setField("contact_phone", e.target.value)}
                disabled={saving}
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs font-semibold text-slate-600">
                {t("offers.inquiries.fields.due_at")}
              </span>
              <input
                type="date"
                className={fieldClass}
                value={form.due_at}
                onChange={(e) => setField("due_at", e.target.value)}
                disabled={saving}
              />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-slate-600">
              {t("offers.inquiries.fields.notes")}
            </span>
            <textarea
              className={`${fieldClass} min-h-[88px]`}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              disabled={saving}
            />
          </label>
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={closeDialog}
              disabled={saving}
              className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {t("Close")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex justify-center rounded-lg border border-transparent bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? t("Loading...") : t("Save")}
            </button>
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={Boolean(statusRow)}
        onClose={closeStatusDialog}
        title={t("offers.inquiries.status_title")}
        size="sm"
        closeOnOutsideClick={!saving}
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            {statusRow?.requesting_company_name}
          </p>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-slate-600">
              {t("offers.inquiries.fields.status")}
            </span>
            <select
              className="offer-select"
              value={statusValue}
              onChange={(e) => setStatusValue(e.target.value)}
              disabled={saving}
            >
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`offers.inquiries.status.${status}`)}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={closeStatusDialog}
              disabled={saving}
              className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {t("Close")}
            </button>
            <button
              type="button"
              onClick={handleStatusSave}
              disabled={saving}
              className="inline-flex justify-center rounded-lg border border-transparent bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0B3A78] disabled:opacity-50"
            >
              {saving ? t("Loading...") : t("Save")}
            </button>
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={Boolean(deleteId)}
        onClose={() => !saving && setDeleteId(null)}
        title={t("offers.inquiries.delete_title")}
        size="sm"
        closeOnOutsideClick={!saving}
      >
        <p className="text-sm text-slate-600">
          {t("offers.inquiries.delete_confirm")}
        </p>
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => setDeleteId(null)}
            disabled={saving}
            className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {t("Close")}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            className="inline-flex justify-center rounded-lg border border-transparent bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
          >
            {saving ? t("Loading...") : t("offers.inquiries.delete")}
          </button>
        </div>
      </PMDialog>
    </div>
  );
}
